// 2026-06-26: Phase 14 — Operator Qualification Register (NADCAP Common Core item 6)
// Track operator certifications + expiry dates + the op_types each cert authorises.
// Data lives in customer SERVER_SCOPE attribute `operator_qualifications` (array).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from "../../Services/app/operatorservice";
import { shiftadd } from "../../Services/app/masterservice";
import Swal from 'sweetalert2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Tooltip, IconButton, Chip, Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

// Common aerospace certifications — operators pick one and add scope ops they're authorised for
const CERT_TYPES = [
  'NDT — Level I (PT/MT/UT/RT/ET)',
  'NDT — Level II (PT/MT/UT/RT/ET)',
  'NDT — Level III',
  'Welder per ASME IX',
  'Welder per AWS D17.1',
  'Heat Treat Operator (AMS2750H)',
  'CMM Operator',
  'Inspection / Final QC',
  'Shot Peening Operator (AMS2430)',
  'Plating / Anodizing Operator',
  'Composite Layup',
  'Brazing Operator',
  'Torque / Bolt-up Operator',
  'Pyrometry Technician (AMS2750H Annex)',
  'Operator — General Machining',
  'Other',
];

const STATUS_INFO = {
  VALID:   { color: BR.green, bg: '#dcfce7', label: 'VALID',   icon: <VerifiedIcon fontSize="small" /> },
  WARNING: { color: BR.amber, bg: '#fef3c7', label: 'EXPIRING', icon: <WarningAmberIcon fontSize="small" /> },
  EXPIRED: { color: BR.red,   bg: '#fee2e2', label: 'EXPIRED', icon: <ErrorOutlineIcon fontSize="small" /> },
};

function computeStatus(expiry) {
  if (!expiry) return 'EXPIRED';
  const e = new Date(expiry);
  if (isNaN(e.getTime())) return 'EXPIRED';
  const days = (e - new Date()) / (24 * 3600 * 1000);
  if (days < 0) return 'EXPIRED';
  if (days <= 30) return 'WARNING';
  return 'VALID';
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysFromNow(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return Math.round((dt - new Date()) / (24 * 3600 * 1000));
}

const emptyQual = {
  id: '',
  operator_id: '',
  operator_name: '',
  certification: '',
  cert_no: '',
  issuing_body: '',
  issue_date: '',
  expiry_date: '',
  scope_op_types: [],
  notes: '',
};

const COMMON_OP_TYPES = [
  'MILLING','TURNING','DRILLING','TAPPING','BORING','GRINDING','REAMING',
  'MANUAL_DEBURR','BRUSH_DEBURR','TUMBLING',
  'HEAT_TREAT','PLATING','ANODIZING','BLACK_OXIDE','PAINTING','WELDING',
  'BOLT_ASSY','PRESS_FIT','ADHESIVE','RIVETING',
  'CMM','GAUGING','VISUAL_INSP','FUNCTIONAL_TEST','HARDNESS_TEST',
  'BOX_PACK','PALLET_PACK','SHRINK_WRAP',
];

export default function OperatorQualifications() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(emptyQual);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await customerbasedshift(customerId, 'operator_qualifications');
      const v = Array.isArray(r?.[0]?.value) ? r[0].value : [];
      setItems(v);
    } catch (e) {
      console.warn('operator qualifications fetch failed:', e?.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const enriched = useMemo(() => items.map(i => ({
    ...i,
    status: computeStatus(i.expiry_date),
    days_until_expiry: daysFromNow(i.expiry_date),
  })), [items]);

  const totals = useMemo(() => {
    const out = { ALL: enriched.length, VALID: 0, WARNING: 0, EXPIRED: 0 };
    enriched.forEach(i => { out[i.status] = (out[i.status] || 0) + 1; });
    return out;
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter(i => {
      if (filter !== 'ALL' && i.status !== filter) return false;
      if (!q) return true;
      return (i.operator_name || '').toLowerCase().includes(q)
          || (i.operator_id || '').toLowerCase().includes(q)
          || (i.certification || '').toLowerCase().includes(q)
          || (i.cert_no || '').toLowerCase().includes(q)
          || (i.scope_op_types || []).join(' ').toLowerCase().includes(q);
    }).sort((a, b) => {
      const order = { EXPIRED: 0, WARNING: 1, VALID: 2 };
      const so = (order[a.status] || 9) - (order[b.status] || 9);
      if (so) return so;
      return (a.days_until_expiry ?? 9999) - (b.days_until_expiry ?? 9999);
    });
  }, [enriched, filter, search]);

  function openAdd() { setDraft({ ...emptyQual, id: `qual-${Date.now()}` }); setEditOpen(true); }
  function openEdit(item) { setDraft({ ...emptyQual, ...item }); setEditOpen(true); }

  async function save() {
    if (!draft.operator_name || !draft.certification) {
      Swal.fire({ icon: 'warning', title: 'Operator and certification required', confirmButtonColor: BR.orange });
      return;
    }
    if (!draft.expiry_date) {
      Swal.fire({ icon: 'warning', title: 'Expiry date required',
                  text: 'A certification without expiry can\'t be tracked for re-validation',
                  confirmButtonColor: BR.orange });
      return;
    }
    setSaving(true);
    try {
      const exists = items.findIndex(i => i.id === draft.id);
      const next = exists === -1 ? [...items, draft]
                                 : items.map((it, idx) => idx === exists ? draft : it);
      await shiftadd({ operator_qualifications: next }, customerId, 'SERVER_SCOPE');
      setItems(next);
      setEditOpen(false);
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                   icon: 'success', title: `${draft.operator_name} · ${draft.certification.split(' —')[0]} saved` });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: e.message, confirmButtonColor: BR.orange });
    } finally {
      setSaving(false);
    }
  }

  async function deleteItem(item) {
    const res = await Swal.fire({
      title: 'Remove certification?',
      html: `<strong>${item.operator_name}</strong><br/>${item.certification}<br/><span style="color:#dc2626">Operator will lose authorisation for ${(item.scope_op_types || []).length} op types.</span>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Remove', confirmButtonColor: BR.red,
      cancelButtonText: 'Cancel', reverseButtons: true, focusCancel: true,
    });
    if (!res.isConfirmed) return;
    try {
      const next = items.filter(i => i.id !== item.id);
      await shiftadd({ operator_qualifications: next }, customerId, 'SERVER_SCOPE');
      setItems(next);
      Swal.fire({ toast: true, position: 'top-end', timer: 1500, showConfirmButton: false,
                   icon: 'success', title: 'Removed' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: e.message, confirmButtonColor: BR.orange });
    }
  }

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>
            🪪 Operator Qualifications
          </h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Operator certifications + expiry + authorised op types · NADCAP Common Core capability #6
          </div>
        </div>
        <button onClick={openAdd}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '9px 16px', background: BR.orange, color: '#fff',
                          border: 'none', borderRadius: 6, fontWeight: 800, fontSize: 13,
                          cursor: 'pointer' }}>
          <AddIcon fontSize="small" /> Add Certification
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { key: 'ALL',     label: 'All', icon: '📋', color: BR.dark },
          { key: 'VALID',   label: 'Valid', icon: '✓', color: BR.green },
          { key: 'WARNING', label: 'Expiring', icon: '⚠', color: BR.amber },
          { key: 'EXPIRED', label: 'Expired', icon: '✗', color: BR.red },
        ].map(t => (
          <div key={t.key} onClick={() => setFilter(t.key)}
               style={{ background: '#fff', border: `2px solid ${filter === t.key ? t.color : BR.border}`,
                        borderRadius: 8, padding: '12px 14px', cursor: 'pointer',
                        boxShadow: filter === t.key ? `0 2px 8px ${t.color}33` : '0 1px 2px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t.icon} {t.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>
              {totals[t.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 10 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search operator name, cert #, certification, scope op…"
               style={{ width: '100%', padding: '8px 12px', border: `1px solid ${BR.border}`,
                         borderRadius: 6, fontSize: 13 }} />
      </div>

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
            No certifications {filter !== 'ALL' ? `in ${filter} state` : 'yet'}. Click "Add Certification" to start.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Status','Operator','Certification','Cert #','Issuing Body','Issued','Expires','Days Left','Authorised Ops',''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10,
                                        fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => {
                const info = STATUS_INFO[i.status] || STATUS_INFO.VALID;
                return (
                  <tr key={i.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: '2px 8px', borderRadius: 4, fontWeight: 800, fontSize: 10,
                                      background: info.bg, color: info.color, border: `1px solid ${info.color}` }}>
                        {info.icon} {info.label}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', fontWeight: 700 }}>
                      {i.operator_name}
                      {i.operator_id && <div style={{ fontSize: 10, color: BR.muted, fontFamily: 'monospace' }}>{i.operator_id}</div>}
                    </td>
                    <td style={{ padding: '9px 10px' }}>{i.certification}</td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: BR.muted }}>{i.cert_no || '—'}</td>
                    <td style={{ padding: '9px 10px', color: BR.muted }}>{i.issuing_body || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>{fmtDate(i.issue_date)}</td>
                    <td style={{ padding: '9px 10px', color: info.color, fontWeight: 700 }}>{fmtDate(i.expiry_date)}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 800, color: info.color }}>
                      {i.days_until_expiry == null ? '—' : `${i.days_until_expiry}d`}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      {(i.scope_op_types || []).slice(0, 4).map(op => (
                        <Chip key={op} label={op} size="small"
                              sx={{ height: 20, fontSize: 10, fontWeight: 700,
                                     background: '#eff6ff', color: '#1e3a8a',
                                     border: '1px solid #93c5fd', marginRight: 0.5, marginBottom: 0.4 }} />
                      ))}
                      {(i.scope_op_types || []).length > 4 && (
                        <Chip label={`+${i.scope_op_types.length - 4}`} size="small"
                              sx={{ height: 20, fontSize: 10, background: '#f3f4f6', color: BR.muted }} />
                      )}
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(i)}>
                        <EditIcon fontSize="small" sx={{ color: '#111' }} />
                      </IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => deleteItem(i)}>
                        <DeleteIcon fontSize="small" sx={{ color: BR.red }} />
                      </IconButton></Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, padding: '8px 12px',
                     background: '#fff7ed', border: `1px solid #fed7aa`, borderRadius: 6 }}>
        <strong style={{ color: '#9a3412' }}>NADCAP note:</strong> An <strong>EXPIRED</strong> certification means the operator must not run or sign-off ops of those types. Future gating: WO claim will be blocked when claiming op_type the operator isn't currently certified for (Common Core #6, audit-grade).
      </div>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {items.some(i => i.id === draft.id) ? `Edit certification` : 'Add Certification'}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <TextField label="Operator Name *" size="small" fullWidth
                       value={draft.operator_name}
                       onChange={e => setDraft({ ...draft, operator_name: e.target.value })} />
            <TextField label="Operator ID / Badge" size="small" fullWidth
                       value={draft.operator_id}
                       onChange={e => setDraft({ ...draft, operator_id: e.target.value })}
                       placeholder="e.g. 0023" />
            <TextField label="Certification *" size="small" fullWidth select
                       value={draft.certification}
                       onChange={e => setDraft({ ...draft, certification: e.target.value })}
                       sx={{ gridColumn: '1 / span 2' }}>
              <MenuItem value="">— Select —</MenuItem>
              {CERT_TYPES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField label="Certificate Number" size="small" fullWidth
                       value={draft.cert_no}
                       onChange={e => setDraft({ ...draft, cert_no: e.target.value })} />
            <TextField label="Issuing Body" size="small" fullWidth
                       value={draft.issuing_body}
                       onChange={e => setDraft({ ...draft, issuing_body: e.target.value })}
                       placeholder="e.g. ASNT, AWS, ASM" />
            <TextField label="Issue Date" size="small" fullWidth type="date"
                       value={draft.issue_date}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, issue_date: e.target.value })} />
            <TextField label="Expiry Date *" size="small" fullWidth type="date"
                       value={draft.expiry_date}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, expiry_date: e.target.value })} />
            <Autocomplete multiple freeSolo size="small" fullWidth
                          options={COMMON_OP_TYPES}
                          value={draft.scope_op_types || []}
                          onChange={(_, v) => setDraft({ ...draft, scope_op_types: v })}
                          renderInput={(params) => (
                            <TextField {...params} label="Authorised Op Types"
                                       placeholder="Pick or type op_type codes" />
                          )}
                          sx={{ gridColumn: '1 / span 2' }} />
            <TextField label="Notes" size="small" fullWidth multiline minRows={2}
                       value={draft.notes}
                       onChange={e => setDraft({ ...draft, notes: e.target.value })}
                       sx={{ gridColumn: '1 / span 2' }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained"
                  sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
